// Sound recipes (audio §4): every ball.io sound is our own WebAudio synthesis in agar's "watery
// bloop" house style (short sine/triangle voices with fast upward pitch sweeps). Nothing here is
// derived from Miniclip audio; only timing, loudness and general character are matched.
//
// - Pure data plus one `render(s)` per slot that schedules nodes on any `BaseAudioContext` (the
//   engine pre-renders each slot once into an `OfflineAudioContext`, and falls back to rendering
//   live into the realtime context until the buffer is ready). No DOM, no globals, no randomness:
//   the same slot always sounds identical. The only noise source is one seeded white-noise buffer
//   (`whiteNoise`, xorshift32, seed `NOISE_SEED`) shared by all recipes.
// - `peak` is the per-sound gain before the master gain (audio §4); `targetDb` is the rendered
//   peak the engine normalises the pre-rendered buffer to (audio §4.1 relative loudness).
// - Slots past the audio.md table are ball.io extensions (money mode, owner task): cash-out
//   start / tick / done / cancel, money gain on an own eat, notice and error. They follow the same
//   recipe style and sit below the core game sounds in loudness, except the cash-out payoff.
//
// Implementer: client-audio (lane A). Consumers: audio/engine.ts, tests.

export type SoundGroup = 'game' | 'menu';

/** Every sound slot, in pre-render priority order (most likely to be needed first). */
export const SOUND_SLOTS = [
  'click',
  'split',
  'shoot',
  'eat',
  'merge',
  'pop',
  'death',
  'cashStart',
  'cashTick',
  'cashOut',
  'cashCancel',
  'moneyGain',
  'notice',
  'error',
  'countdown',
  'countdownEnd',
  'zone',
  'toggle',
  'levelUp',
  // Territory arena (territory-audio.md §3–§4).
  'capture',
  'captureMid',
  'captureBig',
  'kill',
  'deathArena',
  'shatter',
  'spill',
  'collect',
  'homeAlarm',
  'reveal',
] as const;

export type SoundSlot = (typeof SOUND_SLOTS)[number];

/** What a recipe renders into. `t0` is the context time (s) the sound starts at. */
export interface SynthTarget {
  readonly ctx: BaseAudioContext;
  readonly out: AudioNode;
  readonly t0: number;
  /** The shared white-noise buffer (null = no noise layers, e.g. a context without buffers). */
  readonly noise: AudioBuffer | null;
}

export interface Recipe {
  readonly group: SoundGroup;
  /** Sound length (ms, audio §4 "Length"); the pre-rendered buffer is this + RENDER_TAIL_MS. */
  readonly lengthMs: number;
  /** When the last gain envelope reaches silence (ms after the start). */
  readonly endMs: number;
  /** True when a feedback-delay tail rings past `endMs` (up to `lengthMs`). */
  readonly echo?: boolean;
  /** Per-sound peak gain before the master (audio §4 "Peak"). */
  readonly peak: number;
  /** Rendered peak level the pre-rendered buffer is normalised to, dBFS (audio §4.1). */
  readonly targetDb: number;
  readonly render: (s: SynthTarget) => void;
}

/** Silence padding after `lengthMs` in a pre-rendered buffer (audio §7.2: length + 30 ms). */
export const RENDER_TAIL_MS = 30;
/** Seed of the shared noise buffer (audio §7.2). */
export const NOISE_SEED = 0xba11;
/** Envelope floor for exponential ramps (they cannot reach 0). */
const FLOOR = 0.0001;
/** Oscillators stop this long after their envelope ends (audio §4). */
const STOP_PAD = 0.02;

/** `−dB` as a gain factor. */
export function dbGain(db: number): number {
  return 10 ** (db / 20);
}

/** A gain factor in dB (`-Infinity` for 0). */
export function gainDb(g: number): number {
  return 20 * Math.log10(g);
}

/**
 * Deterministic white noise in [-1, 1): xorshift32 from `seed` (audio §7.2). Pure; the engine
 * copies it into one mono AudioBuffer shared by every recipe.
 */
export function whiteNoise(length: number, seed: number = NOISE_SEED): Float32Array {
  const out = new Float32Array(Math.max(0, Math.floor(length)));
  let x = seed >>> 0 || 1;
  for (let i = 0; i < out.length; i++) {
    x ^= x << 13;
    x >>>= 0;
    x ^= x >>> 17;
    x ^= x << 5;
    x >>>= 0;
    out[i] = x / 2147483648 - 1;
  }
  return out;
}

// --- building blocks --------------------------------------------------------------------------

interface BloopOpts {
  /** Start offset (ms) from `t0`. */
  readonly at?: number;
  readonly type?: OscillatorType;
  /** Pitch: f0 → f1 in `upMs`, then → f2 in `downMs` (exponential ramps), then held. */
  readonly f0: number;
  readonly f1: number;
  readonly upMs: number;
  readonly f2: number;
  readonly downMs: number;
  /** Envelope peak (reached at `attackMs`, default `upMs`). */
  readonly peak: number;
  readonly attackMs?: number;
  /** Envelope end (ms after `at`): exponential decay to `floor`. */
  readonly endMs: number;
  readonly floor?: number;
  /** Linear 0 → peak attack instead of the exponential one (click: follows the measured rise). */
  readonly linearAttack?: boolean;
  /** Add a third harmonic at this level, dB below the fundamental (e.g. -12). */
  readonly h3Db?: number;
  readonly lowpassHz?: number;
  /** Pitch multiplier for every frequency (toggle = click × 1.12). */
  readonly pitch?: number;
}

function sweep(p: AudioParam, t: number, o: BloopOpts, mul: number): void {
  const k = mul * (o.pitch ?? 1);
  p.setValueAtTime(o.f0 * k, t);
  p.exponentialRampToValueAtTime(o.f1 * k, t + o.upMs / 1000);
  p.exponentialRampToValueAtTime(o.f2 * k, t + (o.upMs + o.downMs) / 1000);
}

/** One sine/triangle "bloop" voice (audio §4 notation), optionally with h3 and a lowpass. */
function bloop(s: SynthTarget, o: BloopOpts, out: AudioNode = s.out): void {
  const { ctx } = s;
  const t = s.t0 + (o.at ?? 0) / 1000;
  const end = t + o.endMs / 1000;
  const env = ctx.createGain();
  const g = env.gain;
  const tPeak = t + (o.attackMs ?? o.upMs) / 1000;
  if (o.linearAttack) {
    g.setValueAtTime(0, t);
    g.linearRampToValueAtTime(o.peak, tPeak);
  } else {
    g.setValueAtTime(FLOOR, t);
    g.exponentialRampToValueAtTime(o.peak, tPeak);
  }
  g.exponentialRampToValueAtTime(o.floor ?? FLOOR, end);

  let into: AudioNode = env;
  if (o.lowpassHz !== undefined) {
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(o.lowpassHz, t);
    lp.connect(env);
    into = lp;
  }
  env.connect(out);

  const osc = ctx.createOscillator();
  osc.type = o.type ?? 'sine';
  sweep(osc.frequency, t, o, 1);
  osc.connect(into);
  osc.start(t);
  osc.stop(end + STOP_PAD);

  if (o.h3Db !== undefined) {
    const h = ctx.createOscillator();
    h.type = 'sine';
    sweep(h.frequency, t, o, 3);
    const hg = ctx.createGain();
    hg.gain.setValueAtTime(dbGain(o.h3Db), t);
    h.connect(hg);
    hg.connect(into);
    h.start(t);
    h.stop(end + STOP_PAD);
  }
}

interface NoiseOpts {
  readonly at?: number;
  readonly filter: BiquadFilterType;
  /** Filter frequency, optionally swept (exponential) to `toHz` over `sweepMs`. */
  readonly hz: number;
  readonly toHz?: number;
  readonly sweepMs?: number;
  readonly q?: number;
  readonly peak: number;
  readonly attackMs: number;
  readonly linearAttack?: boolean;
  readonly endMs: number;
  /** Where in the shared noise buffer to start (s), so layers do not share the same grain. */
  readonly offset?: number;
}

/** A filtered burst of the shared noise buffer (skipped when there is no buffer). */
function noiseBurst(s: SynthTarget, o: NoiseOpts, out: AudioNode = s.out): void {
  const { ctx, noise } = s;
  if (!noise) return;
  const t = s.t0 + (o.at ?? 0) / 1000;
  const end = t + o.endMs / 1000;
  const src = ctx.createBufferSource();
  src.buffer = noise;
  const f = ctx.createBiquadFilter();
  f.type = o.filter;
  f.frequency.setValueAtTime(o.hz, t);
  if (o.toHz !== undefined)
    f.frequency.exponentialRampToValueAtTime(o.toHz, t + (o.sweepMs ?? o.endMs) / 1000);
  if (o.q !== undefined) f.Q.setValueAtTime(o.q, t);
  const env = ctx.createGain();
  const g = env.gain;
  if (o.linearAttack) {
    g.setValueAtTime(0, t);
    g.linearRampToValueAtTime(o.peak, t + o.attackMs / 1000);
  } else {
    g.setValueAtTime(FLOOR, t);
    g.exponentialRampToValueAtTime(o.peak, t + o.attackMs / 1000);
  }
  g.exponentialRampToValueAtTime(FLOOR, end);
  src.connect(f);
  f.connect(env);
  env.connect(out);
  src.start(t, o.offset ?? 0);
  src.stop(end + STOP_PAD);
}

/** A springy feedback-delay send (levelUp, cashOut): returns the dry bus to feed. */
function echoBus(
  s: SynthTarget,
  delayMs: number,
  feedback: number,
  lowpassHz: number,
  wet: number,
): AudioNode {
  const { ctx } = s;
  const bus = ctx.createGain();
  bus.gain.setValueAtTime(1, s.t0);
  bus.connect(s.out);
  const delay = ctx.createDelay(1);
  delay.delayTime.setValueAtTime(delayMs / 1000, s.t0);
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(lowpassHz, s.t0);
  const fb = ctx.createGain();
  fb.gain.setValueAtTime(feedback, s.t0);
  const wetGain = ctx.createGain();
  wetGain.gain.setValueAtTime(wet, s.t0);
  bus.connect(delay);
  delay.connect(lp);
  lp.connect(fb);
  fb.connect(delay);
  lp.connect(wetGain);
  wetGain.connect(s.out);
  return bus;
}

// --- the recipes (audio §4) -------------------------------------------------------------------

const CLICK: BloopOpts = {
  f0: 360,
  f1: 840,
  upMs: 9,
  f2: 500,
  downMs: 14,
  peak: 0.14,
  endMs: 24,
  floor: 0.001,
  linearAttack: true,
};

function renderClick(s: SynthTarget): void {
  bloop(s, CLICK);
}

function renderToggle(s: SynthTarget): void {
  // Unused slot (agar's checkboxes are silent): the click at ×1.12 pitch, −4 dB.
  bloop(s, { ...CLICK, pitch: 1.12, peak: 0.09 });
}

function renderSplit(s: SynthTarget): void {
  const peak = 0.45;
  // A "whoosh-bloop": the bloop rides on a rising band of noise.
  bloop(s, { f0: 420, f1: 1150, upMs: 22, f2: 720, downMs: 50, peak, endMs: 86, h3Db: -12 });
  noiseBurst(s, {
    filter: 'bandpass',
    hz: 1500,
    toHz: 4500,
    sweepMs: 60,
    q: 1.4,
    peak: 0.35 * peak,
    attackMs: 3,
    linearAttack: true,
    endMs: 80,
  });
}

function renderShoot(s: SynthTarget): void {
  const peak = 0.3;
  bloop(s, { f0: 950, f1: 1500, upMs: 12, f2: 650, downMs: 40, peak, endMs: 56 });
  noiseBurst(s, {
    filter: 'highpass',
    hz: 3000,
    peak: 0.2 * peak,
    attackMs: 0.5,
    linearAttack: true,
    endMs: 4,
    offset: 0.21,
  });
}

function renderEat(s: SynthTarget): void {
  const peak = 0.4;
  // "Gulp": a dark triangle bloop with a wet squelch.
  bloop(s, {
    type: 'triangle',
    f0: 240,
    f1: 520,
    upMs: 12,
    f2: 170,
    downMs: 70,
    peak,
    endMs: 96,
    lowpassHz: 1800,
  });
  noiseBurst(s, {
    filter: 'lowpass',
    hz: 1200,
    peak: 0.25 * peak,
    attackMs: 2,
    linearAttack: true,
    endMs: 47,
    offset: 0.37,
  });
}

function renderMerge(s: SynthTarget): void {
  const peak = 0.28;
  bloop(s, { f0: 300, f1: 620, upMs: 15, f2: 420, downMs: 20, peak, endMs: 42 });
  bloop(s, { at: 50, f0: 380, f1: 780, upMs: 15, f2: 520, downMs: 20, peak, endMs: 42 });
}

const POP_BUBBLES: readonly (readonly [atMs: number, hz: number])[] = [
  [0, 700],
  [30, 560],
  [65, 860],
  [105, 480],
];

function renderPop(s: SynthTarget): void {
  const peak = 0.6;
  const { ctx } = s;
  // (a) Thump.
  const thump = ctx.createOscillator();
  thump.type = 'sine';
  thump.frequency.setValueAtTime(130, s.t0);
  thump.frequency.exponentialRampToValueAtTime(48, s.t0 + 0.14);
  const tg = ctx.createGain();
  tg.gain.setValueAtTime(FLOOR, s.t0);
  tg.gain.exponentialRampToValueAtTime(0.6 * peak, s.t0 + 0.002);
  tg.gain.exponentialRampToValueAtTime(FLOOR, s.t0 + 0.16);
  thump.connect(tg);
  tg.connect(s.out);
  thump.start(s.t0);
  thump.stop(s.t0 + 0.16 + STOP_PAD);
  // (b) Splat: noise through a closing lowpass.
  noiseBurst(s, {
    filter: 'lowpass',
    hz: 5000,
    toHz: 350,
    sweepMs: 260,
    peak: 0.45 * peak,
    attackMs: 1,
    endMs: 280,
    offset: 0.52,
  });
  // (c) Four bubble bloops at fixed times and pitches.
  for (const [at, hz] of POP_BUBBLES) {
    bloop(s, {
      at,
      f0: hz,
      f1: hz * 1.8,
      upMs: 12,
      f2: hz * 1.2,
      downMs: 18,
      peak: 0.25 * peak,
      endMs: 34,
    });
  }
}

function renderDeath(s: SynthTarget): void {
  // The triangle and the fifth (× 0.5) sum to 1.5 × the envelope: 0.45 overall (≈ −7 dBFS).
  const peak = 0.45 / 1.5;
  const { ctx, t0 } = s;
  // Descending "womp" with a vibrato that widens as it falls.
  const env = ctx.createGain();
  env.gain.setValueAtTime(FLOOR, t0);
  env.gain.exponentialRampToValueAtTime(peak, t0 + 0.01);
  env.gain.setValueAtTime(peak, t0 + 0.12);
  env.gain.exponentialRampToValueAtTime(0.001, t0 + 0.7);
  env.connect(s.out);

  const lfo = ctx.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.setValueAtTime(6, t0);
  const depth = ctx.createGain();
  depth.gain.setValueAtTime(12, t0);
  depth.gain.linearRampToValueAtTime(40, t0 + 0.65);
  lfo.connect(depth);

  const main = ctx.createOscillator();
  main.type = 'triangle';
  main.frequency.setValueAtTime(440, t0);
  main.frequency.exponentialRampToValueAtTime(110, t0 + 0.65);
  depth.connect(main.detune);
  main.connect(env);

  const fifth = ctx.createOscillator();
  fifth.type = 'sine';
  fifth.frequency.setValueAtTime(293, t0);
  fifth.frequency.exponentialRampToValueAtTime(73, t0 + 0.65);
  depth.connect(fifth.detune);
  const fg = ctx.createGain();
  fg.gain.setValueAtTime(0.5, t0);
  fifth.connect(fg);
  fg.connect(env);

  const stop = t0 + 0.7 + STOP_PAD;
  for (const o of [lfo, main, fifth]) {
    o.start(t0);
    o.stop(stop);
  }
}

function renderTone(s: SynthTarget, hz: number, endMs: number, peak: number): void {
  // Sine + its octave at −12 dB, 3 ms attack, exponential decay.
  bloop(s, { f0: hz, f1: hz, upMs: 3, f2: hz, downMs: 1, peak, endMs });
  bloop(s, {
    f0: hz * 2,
    f1: hz * 2,
    upMs: 3,
    f2: hz * 2,
    downMs: 1,
    peak: peak * dbGain(-12),
    endMs,
  });
}

function renderZone(s: SynthTarget): void {
  const peak = 0.15;
  for (const [at, hz] of [
    [0, 1000],
    [250, 780],
  ] as const) {
    bloop(s, {
      at,
      type: 'square',
      f0: hz,
      f1: hz,
      upMs: 1,
      f2: hz,
      downMs: 1,
      peak,
      endMs: 18,
      lowpassHz: 2500,
    });
  }
}

function renderLevelUp(s: SynthTarget): void {
  // The echo adds ≈ 1.7 dB on top of the dry bloops; trimmed so the whole lands near 0.8.
  const peak = 0.8 * 0.82;
  const bus = echoBus(s, 57, 0.38, 3000, 0.45);
  const notes: readonly (readonly [number, number, number])[] = [
    [0, 820, 1250],
    [50, 1460, 2100],
    [100, 2500, 4200],
  ];
  for (const [at, f0, f1] of notes) {
    bloop(s, { at, f0, f1, upMs: 12, f2: f1 * 0.9, downMs: 18, peak, endMs: 30, h3Db: -10 }, bus);
  }
}

// --- ball.io extensions: money mode, notices -------------------------------------------------

function renderCashStart(s: SynthTarget): void {
  // "Wind-up": two rising bloops a fourth apart, the hold has begun.
  const peak = 0.25;
  bloop(s, { f0: 520, f1: 880, upMs: 20, f2: 700, downMs: 40, peak, endMs: 64, h3Db: -14 });
  bloop(s, {
    at: 60,
    f0: 700,
    f1: 1180,
    upMs: 20,
    f2: 940,
    downMs: 50,
    peak,
    endMs: 76,
    h3Db: -14,
  });
}

function renderCashTick(s: SynthTarget): void {
  // A soft high "tock" once a second while holding; the engine raises its pitch with progress.
  bloop(s, { f0: 1200, f1: 1600, upMs: 6, f2: 1000, downMs: 30, peak: 0.16, endMs: 42, h3Db: -14 });
}

const CASH_NOTES: readonly (readonly [atMs: number, hz: number])[] = [
  [0, 660],
  [55, 880],
  [110, 1100],
  [165, 1320],
];

function renderCashOut(s: SynthTarget): void {
  // The payoff: a rising four-bloop cascade into a short springy echo.
  // Trimmed for the echo build-up so the cascade peaks near 0.5 (≈ −6 dBFS).
  const peak = 0.5 * 0.84;
  const bus = echoBus(s, 73, 0.32, 3500, 0.4);
  for (const [at, hz] of CASH_NOTES) {
    bloop(
      s,
      { at, f0: hz, f1: hz * 1.5, upMs: 14, f2: hz * 1.25, downMs: 30, peak, endMs: 60, h3Db: -12 },
      bus,
    );
  }
  // A held top note so the cascade lands rather than stops.
  bloop(
    s,
    {
      at: 220,
      f0: 1320,
      f1: 1980,
      upMs: 14,
      f2: 1760,
      downMs: 40,
      peak: 0.8 * peak,
      endMs: 180,
      h3Db: -12,
    },
    bus,
  );
}

function renderCashCancel(s: SynthTarget): void {
  // Two soft falling bloops: the hold was let go.
  const peak = 0.2;
  bloop(s, { type: 'triangle', f0: 700, f1: 760, upMs: 8, f2: 460, downMs: 70, peak, endMs: 84 });
  bloop(s, {
    at: 60,
    type: 'triangle',
    f0: 520,
    f1: 560,
    upMs: 8,
    f2: 330,
    downMs: 80,
    peak,
    endMs: 100,
  });
}

function renderMoneyGain(s: SynthTarget, pitch = 1): void {
  // A bright little "ting" with an inharmonic shimmer, layered after the gulp of an own eat.
  // The three partials sum to ≈ 1.52 × the first: 0.2 overall (≈ −14 dBFS).
  const peak = 0.2 / 1.52;
  bloop(s, { f0: 1400, f1: 1568, upMs: 6, f2: 1568, downMs: 1, peak, endMs: 180, pitch });
  bloop(s, {
    f0: 2100,
    f1: 2349,
    upMs: 6,
    f2: 2349,
    downMs: 1,
    peak: peak * dbGain(-8),
    endMs: 140,
    pitch,
  });
  bloop(s, {
    f0: 6000,
    f1: 6585,
    upMs: 4,
    f2: 6585,
    downMs: 1,
    peak: peak * dbGain(-18),
    endMs: 70,
    pitch,
  });
}

// --- territory arena (territory-audio.md §4): "paper and toy" ---------------------------------

/** capture tiers: < 1 %, 1–5 %, ≥ 5 % of the playable cells (§4 rows 1–3). */
function renderCapture(s: SynthTarget, tier: 0 | 1 | 2): void {
  const peak = tier === 2 ? 0.42 : 0.4;
  const pitch = tier === 0 ? 1 : tier === 1 ? 1.12 : 1.26;
  const out = tier === 2 ? echoBus(s, 70, 0.3, 3000, 0.35) : s.out;
  // (a) Fwump: noise through a lowpass opening 900 Hz → 2.4 kHz in 60 ms.
  noiseBurst(
    s,
    {
      filter: 'lowpass',
      hz: 900 * pitch,
      toHz: 2400 * pitch,
      sweepMs: 60,
      peak: 0.5 * peak,
      attackMs: 4,
      linearAttack: true,
      endMs: 140,
      offset: 0.11,
    },
    out,
  );
  // (b) Chime: two (or three) rising bloops.
  const chime = 0.55 * peak;
  bloop(
    s,
    {
      at: 20,
      f0: 520,
      f1: 780,
      upMs: 15,
      f2: 700,
      downMs: 60,
      peak: chime,
      endMs: 110,
      h3Db: -14,
      pitch,
    },
    out,
  );
  bloop(
    s,
    {
      at: 90,
      f0: 660,
      f1: 990,
      upMs: 15,
      f2: 880,
      downMs: 90,
      peak: chime,
      endMs: 150,
      h3Db: -14,
      pitch,
    },
    out,
  );
  if (tier > 0) {
    bloop(
      s,
      {
        at: 160,
        f0: 830,
        f1: 1240,
        upMs: 15,
        f2: 1110,
        downMs: 110,
        peak: chime,
        endMs: 160,
        h3Db: -14,
        pitch,
      },
      out,
    );
  }
}

function renderKill(s: SynthTarget): void {
  // (a) Snip: two 6 ms highpassed noise ticks; (b) a square bloop; (c) a bright sine bloop.
  const peak = 0.45;
  for (const at of [0, 45]) {
    noiseBurst(s, {
      at,
      filter: 'highpass',
      hz: 4000,
      peak: 0.6 * peak,
      attackMs: 1,
      linearAttack: true,
      endMs: 6,
      offset: 0.2 + at / 1000,
    });
  }
  bloop(s, {
    at: 50,
    type: 'square',
    f0: 700,
    f1: 1400,
    upMs: 20,
    f2: 1050,
    downMs: 80,
    peak: 0.55 * peak,
    endMs: 130,
    lowpassHz: 3000,
  });
  bloop(s, {
    at: 110,
    f0: 1400,
    f1: 2100,
    upMs: 15,
    f2: 1870,
    downMs: 120,
    peak: 0.8 * peak,
    endMs: 160,
  });
}

/** The crackle pulse times (ms) of the paper crumple (§4 `death`). */
const CRUMPLE_PULSES: readonly number[] = [0, 35, 60, 110, 150, 205, 260, 330, 400];

/** A paper crumple: bandpassed noise swept down, gated by a fixed crackle of pulses. */
function crumple(s: SynthTarget, peak: number, pulses: number): void {
  const { ctx, noise } = s;
  if (!noise) return;
  const t = s.t0;
  const src = ctx.createBufferSource();
  src.buffer = noise;
  const f = ctx.createBiquadFilter();
  f.type = 'bandpass';
  f.frequency.setValueAtTime(2200, t);
  f.frequency.exponentialRampToValueAtTime(600, t + 0.4);
  f.Q.setValueAtTime(2, t);
  const env = ctx.createGain();
  const g = env.gain;
  g.setValueAtTime(FLOOR, t);
  const n = Math.min(pulses, CRUMPLE_PULSES.length);
  let end = t;
  for (let i = 0; i < n; i++) {
    const at = t + (CRUMPLE_PULSES[i] as number) / 1000;
    const level = peak * (1 - (0.7 * i) / (CRUMPLE_PULSES.length - 1));
    g.setValueAtTime(FLOOR, at);
    g.linearRampToValueAtTime(level, at + 0.002);
    g.exponentialRampToValueAtTime(FLOOR, at + 0.027);
    end = at + 0.027;
  }
  src.connect(f);
  f.connect(env);
  env.connect(s.out);
  src.start(t, 0.37);
  src.stop(end + STOP_PAD);
}

function renderDeathArena(s: SynthTarget): void {
  const peak = 0.45;
  crumple(s, peak, CRUMPLE_PULSES.length);
  // (b) Drop: a falling triangle.
  const { ctx } = s;
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(392, s.t0);
  osc.frequency.exponentialRampToValueAtTime(98, s.t0 + 0.6);
  const env = ctx.createGain();
  env.gain.setValueAtTime(FLOOR, s.t0);
  env.gain.linearRampToValueAtTime(0.6 * peak, s.t0 + 0.01);
  env.gain.exponentialRampToValueAtTime(0.001, s.t0 + 0.65);
  osc.connect(env);
  env.connect(s.out);
  osc.start(s.t0);
  osc.stop(s.t0 + 0.65 + STOP_PAD);
}

function renderShatter(s: SynthTarget): void {
  // The crumple alone, its first six pulses, 10 dB under `death`.
  crumple(s, 0.14, 6);
}

const SPILL_PINGS: readonly (readonly [atMs: number, gain: number])[] = [
  [0, 1],
  [28, 0.8],
  [61, 0.65],
  [97, 0.5],
  [140, 0.4],
];

function renderSpill(s: SynthTarget): void {
  const peak = 0.22;
  noiseBurst(s, {
    filter: 'highpass',
    hz: 5000,
    peak: 0.3 * peak,
    attackMs: 1,
    linearAttack: true,
    endMs: 20,
    offset: 0.53,
  });
  for (const [at, gain] of SPILL_PINGS) {
    bloop(s, {
      at,
      f0: 2400,
      f1: 2900,
      upMs: 4,
      f2: 2600,
      downMs: 70,
      peak: gain * peak * 0.9,
      endMs: 85,
      h3Db: -18,
    });
  }
}

function renderHomeAlarm(s: SynthTarget): void {
  const peak = 0.12;
  for (const [at, hz] of [
    [0, 1180],
    [90, 940],
  ] as const) {
    bloop(s, {
      at,
      type: 'square',
      f0: hz,
      f1: hz,
      upMs: 2,
      f2: hz,
      downMs: 1,
      peak,
      attackMs: 2,
      linearAttack: true,
      endMs: 25,
      lowpassHz: 2500,
    });
  }
}

function renderReveal(s: SynthTarget): void {
  // A soft tick per results row; the fourth row plays it at 4/3 rate (1320 Hz).
  bloop(s, {
    f0: 990,
    f1: 990,
    upMs: 2,
    f2: 990,
    downMs: 1,
    peak: 0.08 / 1.25,
    attackMs: 2,
    linearAttack: true,
    endMs: 60,
    h3Db: -12,
  });
}

function renderNotice(s: SynthTarget): void {
  // Neutral two-step "bloop-bloop", rising: something to read.
  const peak = 0.18;
  bloop(s, { f0: 640, f1: 760, upMs: 10, f2: 740, downMs: 20, peak, endMs: 90, h3Db: -16 });
  bloop(s, {
    at: 100,
    f0: 860,
    f1: 1010,
    upMs: 10,
    f2: 988,
    downMs: 20,
    peak,
    endMs: 120,
    h3Db: -16,
  });
}

function renderError(s: SynthTarget): void {
  // Low double "bonk", falling: not now / that failed.
  const peak = 0.2;
  bloop(s, {
    type: 'triangle',
    f0: 300,
    f1: 330,
    upMs: 6,
    f2: 220,
    downMs: 64,
    peak,
    endMs: 80,
    lowpassHz: 2000,
  });
  bloop(s, {
    at: 110,
    type: 'triangle',
    f0: 260,
    f1: 285,
    upMs: 6,
    f2: 190,
    downMs: 64,
    peak,
    endMs: 90,
    lowpassHz: 2000,
  });
}

/** Rendered peak levels, dBFS (audio §4.1 for the core slots; `20·log10(peak)` otherwise). */
function db(peak: number): number {
  return Math.round(gainDb(peak) * 10) / 10;
}

export const RECIPES: Readonly<Record<SoundSlot, Recipe>> = Object.freeze({
  click: { group: 'menu', lengthMs: 30, endMs: 24, peak: 0.14, targetDb: -17, render: renderClick },
  split: { group: 'game', lengthMs: 90, endMs: 86, peak: 0.45, targetDb: -7, render: renderSplit },
  shoot: { group: 'game', lengthMs: 60, endMs: 56, peak: 0.3, targetDb: -11, render: renderShoot },
  eat: { group: 'game', lengthMs: 100, endMs: 96, peak: 0.4, targetDb: -8, render: renderEat },
  merge: { group: 'game', lengthMs: 95, endMs: 92, peak: 0.28, targetDb: -11, render: renderMerge },
  pop: { group: 'game', lengthMs: 320, endMs: 280, peak: 0.6, targetDb: -4.5, render: renderPop },
  death: {
    group: 'game',
    lengthMs: 700,
    endMs: 700,
    peak: 0.45,
    targetDb: -7,
    render: renderDeath,
  },
  cashStart: {
    group: 'game',
    lengthMs: 140,
    endMs: 136,
    peak: 0.25,
    targetDb: -12,
    render: renderCashStart,
  },
  cashTick: {
    group: 'game',
    lengthMs: 45,
    endMs: 42,
    peak: 0.16,
    targetDb: -16,
    render: renderCashTick,
  },
  cashOut: {
    group: 'game',
    lengthMs: 650,
    endMs: 400,
    echo: true,
    peak: 0.5,
    targetDb: -6,
    render: renderCashOut,
  },
  cashCancel: {
    group: 'game',
    lengthMs: 165,
    endMs: 160,
    peak: 0.2,
    targetDb: -14,
    render: renderCashCancel,
  },
  moneyGain: {
    group: 'game',
    lengthMs: 185,
    endMs: 180,
    peak: 0.2,
    targetDb: -14,
    render: (s: SynthTarget) => renderMoneyGain(s),
  },
  notice: {
    group: 'game',
    lengthMs: 225,
    endMs: 220,
    peak: 0.18,
    targetDb: db(0.18),
    render: renderNotice,
  },
  error: {
    group: 'menu',
    lengthMs: 205,
    endMs: 200,
    peak: 0.2,
    targetDb: -14,
    render: renderError,
  },
  countdown: {
    group: 'game',
    lengthMs: 110,
    endMs: 110,
    peak: 0.25,
    targetDb: db(0.25),
    render: (s: SynthTarget) => renderTone(s, 880, 110, 0.25),
  },
  countdownEnd: {
    group: 'game',
    lengthMs: 260,
    endMs: 260,
    peak: 0.25,
    targetDb: db(0.25),
    render: (s: SynthTarget) => renderTone(s, 1320, 260, 0.25),
  },
  zone: {
    group: 'game',
    lengthMs: 270,
    endMs: 268,
    peak: 0.15,
    targetDb: db(0.15),
    render: renderZone,
  },
  toggle: {
    group: 'menu',
    lengthMs: 30,
    endMs: 24,
    peak: 0.09,
    targetDb: db(0.09),
    render: renderToggle,
  },
  levelUp: {
    group: 'menu',
    lengthMs: 550,
    endMs: 130,
    echo: true,
    peak: 0.8,
    targetDb: db(0.8),
    render: renderLevelUp,
  },
  capture: {
    group: 'game',
    lengthMs: 260,
    endMs: 240,
    peak: 0.4,
    targetDb: -9,
    render: (s: SynthTarget) => renderCapture(s, 0),
  },
  captureMid: {
    group: 'game',
    lengthMs: 330,
    endMs: 320,
    peak: 0.4,
    targetDb: -9,
    render: (s: SynthTarget) => renderCapture(s, 1),
  },
  captureBig: {
    group: 'game',
    lengthMs: 520,
    endMs: 320,
    echo: true,
    peak: 0.42,
    targetDb: -8.5,
    render: (s: SynthTarget) => renderCapture(s, 2),
  },
  kill: { group: 'game', lengthMs: 300, endMs: 270, peak: 0.45, targetDb: -7, render: renderKill },
  deathArena: {
    group: 'game',
    lengthMs: 700,
    endMs: 655,
    peak: 0.45,
    targetDb: -7,
    render: renderDeathArena,
  },
  shatter: {
    group: 'game',
    lengthMs: 250,
    endMs: 232,
    peak: 0.14,
    targetDb: -17,
    render: renderShatter,
  },
  spill: {
    group: 'game',
    lengthMs: 230,
    endMs: 225,
    peak: 0.22,
    targetDb: -14,
    render: renderSpill,
  },
  collect: {
    group: 'game',
    lengthMs: 185,
    endMs: 180,
    peak: 0.2,
    targetDb: -14,
    render: (s: SynthTarget) => renderMoneyGain(s, 1.06),
  },
  homeAlarm: {
    group: 'game',
    lengthMs: 120,
    endMs: 115,
    peak: 0.12,
    targetDb: -19,
    render: renderHomeAlarm,
  },
  reveal: {
    group: 'menu',
    lengthMs: 60,
    endMs: 60,
    peak: 0.08,
    targetDb: -22,
    render: renderReveal,
  },
} satisfies Record<SoundSlot, Recipe>);

/**
 * Scales `data` in place so its absolute peak sits at `targetDb` dBFS; returns the applied gain
 * (1 for silence). Pre-rendered buffers go through this so §4.1's relative loudness holds no
 * matter how the layers of a recipe happen to sum.
 */
export function normalizePeak(data: Float32Array, targetDb: number): number {
  let max = 0;
  for (let i = 0; i < data.length; i++) {
    const a = Math.abs(data[i] as number);
    if (a > max) max = a;
  }
  if (!(max > 0)) return 1;
  const k = dbGain(targetDb) / max;
  for (let i = 0; i < data.length; i++) data[i] = (data[i] as number) * k;
  return k;
}
